/**
 * 文本属性面板（富文本版）
 *
 * 新增控件：
 *  - 文本类型（标题/副标题/正文）
 *  - 列表类型（无/有序/无序/自定义）+ 自定义符号 + 列表间距
 *  - 段落间距（非列表状态）
 */
import { Card, ListBox, Select } from '@heroui/react';
import { useState, useEffect } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { ElementPatch, TextElement } from '@/components/canvas/document/publicTypes';
import { boxSizeControlPatchToElementPatch, layoutControlPatchToElementPatch } from '@/components/canvas/elements/model';
import {
  AppearanceSettings,
  BoxSizeSettings,
  LayoutSettings,
  TextSettings,
  appearanceValueFromAppearance,
  appearanceValueToAppearancePatch,
  boxSizeSettingsValueFromElement,
  layoutSettingsValueFromElement,
  type AppearanceSettingsValue,
  type BoxSizeSettingsValue,
  type CanvasAlignAction,
  type LayoutSettingsValue,
  type RichTextTextSettingsPatch,
} from '@/components/canvas/shared/controls';
import {
  TEXT_BLOCK_KINDS,
  getBlockKindDisplayName,
  getFontSizeForBlockKind,
  TEXT_BLOCK_LAYOUT_DEFAULTS,
} from '@/components/canvas/elements/text/constants';
import {
  detectBlockKind,
  type TextBlockKind,
} from '@/components/canvas/elements/text/utils/textContent';
import {
  applyBoldToSelection,
} from '@/components/canvas/elements/text/controllers/textSelection';
import { normalizeElementText } from '@/components/canvas/elements/text/model/textNormalize';
import { getTextLayoutFromElement } from '@/components/canvas/elements/text/model/textStyles';
import { getTextExpandSize } from '@/components/canvas/elements/text/utils/textMeasure';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export interface TextPropertiesProps {
  element: TextElement;
}

export function TextProperties({ element: el }: TextPropertiesProps) {
  const { updateElement, editingTextId, getCurrentPage } = useCanvas();
  const patch = (partial: ElementPatch<'text'>) => updateElement(el.id, partial);

  const appearanceValue = appearanceValueFromAppearance(el.appearance);

  const handleLayoutChange = (partial: Partial<LayoutSettingsValue>) => {
    patch(layoutControlPatchToElementPatch(el, partial) as ElementPatch<'text'>);
  };

  const handleBoxSizeChange = (partial: Partial<BoxSizeSettingsValue>) => {
    patch(boxSizeControlPatchToElementPatch(partial) as ElementPatch<'text'>);
  };

  const handleAppearanceChange = (partial: Partial<AppearanceSettingsValue>) => {
    const next = { ...appearanceValue, ...partial };
    patch({
      appearance: { ...el.appearance, ...appearanceValueToAppearancePatch(next, el.appearance) },
    } as ElementPatch<'text'>);
  };

  const handleAlignTextToCanvas = (action: CanvasAlignAction) => {
    const page = getCurrentPage();
    if (!page) return;
    const { width: cw, height: ch } = page.size;
    const w = el.size.width;
    const h = el.size.height;
    let x = el.layout.x;
    let y = el.layout.y;
    if (action === 'left') x = 0;
    if (action === 'right') x = cw - w;
    if (action === 'top') y = 0;
    if (action === 'bottom') y = ch - h;
    if (action === 'center') {
      x = (cw - w) / 2;
      y = (ch - h) / 2;
    }
    patch({ layout: { x, y } });
  };

  const isEditingThis = editingTextId === el.id;

  const [selectionIsBold, setSelectionIsBold] = useState(false);
  useEffect(() => {
    if (!isEditingThis) return;
    const update = () => {
      const sel = window.getSelection();
      const anchorNode = sel?.anchorNode;
      const anchorEl: HTMLElement | null = anchorNode
        ? anchorNode.nodeType === Node.ELEMENT_NODE
          ? (anchorNode as unknown as HTMLElement)
          : (anchorNode.parentElement as HTMLElement | null)
        : null;
      setSelectionIsBold(
        anchorEl ? parseInt(getComputedStyle(anchorEl).fontWeight, 10) >= 700 : false,
      );
    };
    document.addEventListener('selectionchange', update);
    update();
    return () => document.removeEventListener('selectionchange', update);
  }, [isEditingThis]);

  const textLayout = getTextLayoutFromElement(el);
  const currentBlockKind = detectBlockKind(el.data.document);
  const handleBlockKindChange = (blockKind: TextBlockKind) => {
    void getFontSizeForBlockKind(blockKind);
    void TEXT_BLOCK_LAYOUT_DEFAULTS[blockKind].fontWeight;
    const nextText = normalizeElementText({
      ...el.data,
      document: {
        version: 1,
        blocks: el.data.document.blocks.map((block) => ({ ...block, type: blockKind })),
      },
    }, { shellStyle: el.typography });
    patch({
      data: nextText,
    });
  };

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-4" data-text-properties-panel>
      <BoxSizeSettings value={boxSizeSettingsValueFromElement(el)} onChange={handleBoxSizeChange} />
      <LayoutSettings
        value={layoutSettingsValueFromElement(el)}
        onChange={handleLayoutChange}
        onAlignToCanvas={handleAlignTextToCanvas}
      />
      <AppearanceSettings value={appearanceValue} onChange={handleAppearanceChange} />

      <TextSettings
        title="文本"
        value={{
          fontFamily: textLayout.typography.fontFamily ?? '',
          fontWeight: textLayout.typography.fontWeight,
          fontSize: textLayout.typography.fontSize ?? 14,
          lineHeight: textLayout.typography.lineHeight,
          letterSpacing: textLayout.typography.letterSpacing,
          textColor: textLayout.typography.color ?? '#000000',
          textAlign: textLayout.paragraph.textAlign,
          verticalAlign: el.paragraph.verticalAlign,
          writingMode: textLayout.writingMode,
        }}
        onChange={(p) => {
          const richPatch = p as RichTextTextSettingsPatch;
          const nextPatch: ElementPatch<'text'> = {
            typography: {
              ...(richPatch.fontFamily !== undefined ? { fontFamily: richPatch.fontFamily } : {}),
              ...(richPatch.fontWeight !== undefined ? { fontWeight: richPatch.fontWeight } : {}),
              ...(richPatch.fontSize !== undefined ? { fontSize: richPatch.fontSize } : {}),
              ...(richPatch.lineHeight !== undefined ? { lineHeight: richPatch.lineHeight } : {}),
              ...(richPatch.letterSpacing !== undefined
                ? { letterSpacing: richPatch.letterSpacing }
                : {}),
              ...(richPatch.color !== undefined ? { color: richPatch.color } : {}),
            },
            paragraph: {
              ...(richPatch.textAlign !== undefined ? { textAlign: richPatch.textAlign } : {}),
              ...('writingMode' in richPatch ? { writingMode: richPatch.writingMode } : {}),
              ...(richPatch.verticalAlign !== undefined
                ? { verticalAlign: richPatch.verticalAlign }
                : {}),
            },
          };
          if (richPatch.writingMode === 'vertical-rl') {
            const expand = getTextExpandSize(el, nextPatch);
            if (expand.width && expand.width > el.size.width) {
              const deltaW = expand.width - el.size.width;
              nextPatch.layout = { ...(nextPatch.layout ?? {}), x: el.layout.x - deltaW };
              nextPatch.size = { ...(nextPatch.size ?? {}), width: expand.width };
            }
          }
          patch(nextPatch);
        }}
        richTextEditing={{
          isActive: isEditingThis,
          selectionBold: selectionIsBold,
          onBoldPress: () => {
            applyBoldToSelection();
          },
        }}
      />

      <Card>
        <Card.Header>
          <Card.Title>文本类型</Card.Title>
        </Card.Header>
        <Card.Content className="space-y-2">
          <Select
            aria-label="文本类型"
            value={currentBlockKind}
            onChange={(key) => {
              if (TEXT_BLOCK_KINDS.includes(key as TextBlockKind)) {
                handleBlockKindChange(key as TextBlockKind);
              }
            }}
          >
            <Select.Trigger className="h-8">
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {TEXT_BLOCK_KINDS.map((kind: TextBlockKind) => (
                  <ListBox.Item key={kind} id={kind} textValue={getBlockKindDisplayName(kind)}>
                    {getBlockKindDisplayName(kind)}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        </Card.Content>
      </Card>
    </div>
  );
}
